import { useEffect, useState } from 'react'
import { Box, Flex, Text, Input } from '@chakra-ui/react'
import { CatalogoBasePage, type ColumnConfig, type FieldConfig } from '../../../components/CatalogoBasePage'
import { measurementUnitsCatalogService } from '../../../api/services'
import {
  MeasurementCategory,
  type MeasurementUnitItem,
} from '../../../api/models/maestros.models'
import { themeColors, brand } from '../../../theme'

const CATEGORY_LABEL: Record<MeasurementCategory, string> = {
  [MeasurementCategory.Weight]: 'Peso',
  [MeasurementCategory.Volume]: 'Volumen',
  [MeasurementCategory.Length]: 'Longitud',
}

const CATEGORY_COLOR: Record<MeasurementCategory, string> = {
  [MeasurementCategory.Weight]: '#3d6fa8',
  [MeasurementCategory.Volume]: '#8B5CF6',
  [MeasurementCategory.Length]: '#d49a2a',
}

const BoolBadge = ({ value, trueText = 'Sí', falseText = 'No', trueColor = '#2a8a87' }: { value: boolean; trueText?: string; falseText?: string; trueColor?: string }) => (
  <Box
    display="inline-block"
    px={2}
    py={0.5}
    borderRadius="md"
    fontSize="10px"
    fontWeight="700"
    bg={value ? `${trueColor}20` : 'rgba(181, 170, 157,0.15)'}
    color={value ? trueColor : '#b5aa9d'}
  >
    {value ? trueText : falseText}
  </Box>
)

const columns: ColumnConfig<MeasurementUnitItem>[] = [
  { header: 'Nombre', render: (u) => <Text fontSize="sm" color={themeColors.text} fontWeight="600">{u.name}</Text> },
  { header: 'Abreviatura', render: (u) => <Text fontFamily="monospace" fontSize="sm" color={themeColors.text}>{u.abbreviation}</Text> },
  {
    header: 'Categoría',
    render: (u) => (
      <Box
        display="inline-block"
        px={2}
        py={0.5}
        borderRadius="md"
        fontSize="10px"
        fontWeight="700"
        bg={`${CATEGORY_COLOR[u.category]}20`}
        color={CATEGORY_COLOR[u.category]}
      >
        {CATEGORY_LABEL[u.category]}
      </Box>
    ),
  },
  { header: 'Factor', render: (u) => <Text fontSize="sm" fontFamily="monospace" color={themeColors.textSecondary}>{u.conversionFactorToBase}</Text> },
  { header: 'Base', render: (u) => <BoolBadge value={u.isBaseUnit} trueText="Base" falseText="-" /> },
  { header: 'Activo', render: (u) => <BoolBadge value={u.isActive} /> },
  { header: 'Orden', render: (u) => <Text fontSize="sm" color={themeColors.textSecondary}>{u.sortOrder}</Text> },
]

const fields: FieldConfig<MeasurementUnitItem>[] = [
  { name: 'name', label: 'Nombre', type: 'text', required: true, placeholder: 'ej. Kilogramo' },
  { name: 'abbreviation', label: 'Abreviatura', type: 'text', required: true, placeholder: 'ej. kg' },
  {
    name: 'category',
    label: 'Categoría',
    type: 'select',
    required: true,
    options: [
      { value: MeasurementCategory.Weight, label: 'Peso' },
      { value: MeasurementCategory.Volume, label: 'Volumen' },
      { value: MeasurementCategory.Length, label: 'Longitud' },
    ],
  },
  { name: 'conversionFactorToBase', label: 'Factor de conversión a base', type: 'number', required: true, step: 0.000001 },
  { name: 'sortOrder', label: 'Orden', type: 'number', min: 0 },
  { name: 'isBaseUnit', label: '¿Es unidad base?', type: 'checkbox' },
  { name: 'isActive', label: '¿Activo?', type: 'checkbox' },
]

// ─── Conversion widget ───
function ConversionWidget({ colors }: { colors: typeof themeColors }) {
  const [units, setUnits] = useState<MeasurementUnitItem[]>([])
  const [category, setCategory] = useState<MeasurementCategory>(MeasurementCategory.Weight)
  const [fromUnitId, setFromUnitId] = useState('')
  const [toUnitId, setToUnitId] = useState('')
  const [inputValue, setInputValue] = useState('1')
  const [output, setOutput] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    measurementUnitsCatalogService
      .getAll({ page: 1, pageSize: 200, isActive: true })
      .then((r) => setUnits(r.items))
      .catch(() => setUnits([]))
  }, [])

  const filtered = units.filter((u) => u.category === category)

  useEffect(() => {
    setFromUnitId('')
    setToUnitId('')
    setOutput(null)
  }, [category])

  const handleConvert = async () => {
    if (!fromUnitId || !toUnitId || !inputValue) return
    const v = Number(inputValue)
    if (Number.isNaN(v)) return
    setLoading(true)
    try {
      const res = await measurementUnitsCatalogService.convert({
        fromUnitId,
        toUnitId,
        value: v,
      })
      setOutput(res.outputValue)
    } catch {
      setOutput(null)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box
      bg={colors.bgPanel}
      border="1px solid"
      borderColor={colors.border}
      borderRadius="xl"
      p={4}
    >
      <Text fontSize="sm" fontWeight="700" color={colors.text} mb={3}>
        Conversor de Unidades
      </Text>
      <Flex gap={3} flexWrap="wrap" alignItems="flex-end">
        <Box flex="1" minW="140px">
          <Text fontSize="xs" color={colors.textMuted} mb={1}>Categoría</Text>
          <Box
            as="select"
            value={String(category)}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setCategory(Number(e.target.value) as MeasurementCategory)}
            width="100%"
            px={3}
            py={2}
            borderRadius="md"
            border="1px solid"
            borderColor={colors.border}
            bg={colors.bgCard}
            fontSize="sm"
            color={colors.text}
          >
            <option value={MeasurementCategory.Weight}>Peso</option>
            <option value={MeasurementCategory.Volume}>Volumen</option>
            <option value={MeasurementCategory.Length}>Longitud</option>
          </Box>
        </Box>
        <Box flex="1" minW="140px">
          <Text fontSize="xs" color={colors.textMuted} mb={1}>Desde</Text>
          <Box
            as="select"
            value={fromUnitId}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFromUnitId(e.target.value)}
            width="100%"
            px={3}
            py={2}
            borderRadius="md"
            border="1px solid"
            borderColor={colors.border}
            bg={colors.bgCard}
            fontSize="sm"
            color={colors.text}
          >
            <option value="">Seleccione...</option>
            {filtered.map((u) => (
              <option key={u.id} value={u.id}>{u.name} ({u.abbreviation})</option>
            ))}
          </Box>
        </Box>
        <Box flex="1" minW="140px">
          <Text fontSize="xs" color={colors.textMuted} mb={1}>Hasta</Text>
          <Box
            as="select"
            value={toUnitId}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setToUnitId(e.target.value)}
            width="100%"
            px={3}
            py={2}
            borderRadius="md"
            border="1px solid"
            borderColor={colors.border}
            bg={colors.bgCard}
            fontSize="sm"
            color={colors.text}
          >
            <option value="">Seleccione...</option>
            {filtered.map((u) => (
              <option key={u.id} value={u.id}>{u.name} ({u.abbreviation})</option>
            ))}
          </Box>
        </Box>
        <Box flex="1" minW="100px">
          <Text fontSize="xs" color={colors.textMuted} mb={1}>Valor</Text>
          <Input
            type="number"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            fontSize="sm"
          />
        </Box>
        <Box
          as="button"
          type="button"
          px={5}
          py={2.5}
          borderRadius="md"
          fontSize="sm"
          fontWeight="600"
          color="white"
          bg={brand.primary}
          cursor={loading ? 'not-allowed' : 'pointer'}
          opacity={loading ? 0.7 : 1}
          _hover={{ opacity: 0.9 }}
          onClick={handleConvert}
        >
          {loading ? '...' : 'Convertir'}
        </Box>
      </Flex>
      {output !== null && (
        <Box mt={3} p={3} bg={colors.bgCard} borderRadius="md">
          <Text fontSize="xs" color={colors.textMuted}>Resultado</Text>
          <Text fontSize="lg" fontWeight="700" color={brand.primary} fontFamily="monospace">
            {output}
          </Text>
        </Box>
      )}
    </Box>
  )
}

export default function MeasurementUnitsPage() {
  const [categoryFilter, setCategoryFilter] = useState<string>('')
  const colors = themeColors

  const filterParams: Record<string, unknown> = {}
  if (categoryFilter) filterParams.category = Number(categoryFilter)

  return (
    <CatalogoBasePage<MeasurementUnitItem>
      title="Unidades de Medida"
      subtitle="Gestiona el catálogo de unidades de medida y sus conversiones"
      entityName="unidad"
      service={measurementUnitsCatalogService as never}
      columns={columns}
      fields={fields}
      keyExtractor={(u) => u.id}
      idExtractor={(u) => u.id}
      searchPlaceholder="Buscar por nombre o abreviatura..."
      defaultValues={{ isActive: true, isBaseUnit: false, conversionFactorToBase: 1, sortOrder: 0 }}
      filterParams={filterParams}
      modalSize="lg"
      extraHeader={<ConversionWidget colors={colors} />}
      toolbarExtra={
        <Box
          as="select"
          value={categoryFilter}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setCategoryFilter(e.target.value)}
          px={3}
          py={2}
          borderRadius="lg"
          border="none"
          bg={colors.bgHover}
          fontSize="sm"
          color={colors.text}
          cursor="pointer"
        >
          <option value="">Todas las categorías</option>
          <option value={MeasurementCategory.Weight}>Peso</option>
          <option value={MeasurementCategory.Volume}>Volumen</option>
          <option value={MeasurementCategory.Length}>Longitud</option>
        </Box>
      }
      createPermission="masters.write"
      updatePermission="masters.write"
      deletePermission="masters.write"
    />
  )
}
